import {
  AttributeDefinitionDTO,
  leaderboardClient as defaultLeaderboardClient,
} from '@neptune/shared/core-apis-leaderboard-domain';

import { isNotUndefined } from 'common/tsHelpers';

import config from 'config';

import {
  type AttributePatternWidgetSource,
  type AttributeWidgetSource,
  WidgetSourceType,
} from './core-widget-types';

type Dependencies = {
  leaderboardClient: {
    queryAttributeDefinitionsWithinProject: typeof defaultLeaderboardClient.queryAttributeDefinitionsWithinProject;
  };
};

export const createWidgetAttributeResolver = (dependencies: Partial<Dependencies> = {}) => {
  const { leaderboardClient = defaultLeaderboardClient } = dependencies;

  async function fromSources({
    projectIds,
    experimentIds,
    sources,
  }: {
    projectIds: string[];
    experimentIds: string[];
    sources: AttributePatternWidgetSource[];
  }) {
    const allPromises = sources.map((source) =>
      resolveSourceAttributes({ projectIds, experimentIds, source }),
    );

    const resolvedAttributes = await Promise.all(allPromises);

    return resolvedAttributes.filter(isNotUndefined).flat();
  }

  async function resolveSourceAttributes({
    projectIds,
    experimentIds,
    source,
  }: {
    projectIds: string[];
    experimentIds: string[];
    source: AttributePatternWidgetSource;
  }) {
    let nextPage;
    let entries: AttributeDefinitionDTO[] = [];

    do {
      const result = await leaderboardClient.queryAttributeDefinitionsWithinProject({
        query: {
          nextPage,
          attributeFilter: [{ attributeType: 'floatSeries' }],
          attributeNameFilter: {
            mustMatchRegexes: [source.value],
          },
          experimentIdsFilter: experimentIds,
          projectIdentifiers: projectIds,
        },
      });

      nextPage = result.nextPage;
      entries = entries.concat(result.entries);
    } while (nextPage?.nextPageToken && entries.length <= (config.chartMaxMetricsLimit ?? 2_000));

    return mapFromAPIAttributesToDomain(entries);
  }

  function mapFromAPIAttributesToDomain(
    attributes: AttributeDefinitionDTO[],
  ): AttributeWidgetSource[] {
    return attributes.map((attribute) => ({
      type: WidgetSourceType.ATTRIBUTE,
      value: attribute.name,
      metadata: {
        attributeType: 'floatSeries',
      },
    }));
  }

  return { fromSources };
};

export const widgetAttributeResolver = createWidgetAttributeResolver();
